import React from "react";
import { bool, elementType, node, oneOf, string } from "prop-types";
import { classes } from "../utils/classes";
import forwardRefPropType from "../utils/forward-ref";
import pureProps, { deepMergeProps } from "../utils/pureProps";
import { spacingClassName, spacingProps, spacingPropTypes } from "../props/spacing";
import { utilityClassName, utilityProps, utilityPropTypes } from "../props/utility";
import { sxClassName, sxProps, sxPropTypes } from "../props/sx";

export const Container = ({
	Tag = "div",
	isFluid = false,
	utility = { ...utilityProps },
	sx = { ...sxProps },
	spacing = { ...spacingProps },
	className = "",
	children,
}) => {
	return (
		<Tag
			className={classes(
				"grid-container",
				isFluid && "fluid",
				...utilityClassName(utility),
				...sxClassName(sx),
				...spacingClassName(spacing),
				className,
			)}
		>
			{children}
		</Tag>
	);
};

Container.propTypes = {
	Tag: string,
	isFluid: bool,
	className: string,
	children: node.isRequired,
	utility: utilityPropTypes,
	sx: sxPropTypes,
	spacing: spacingPropTypes,
};

export const Row = ({
	forwardRef,
	Tag = "div",
	position = "grid-x",
	smCols,
	mdCols,
	lgCols,
	mx = false,
	px = false,
	my = false,
	py = false,
	center = false,
	middle = false,
	justify = false,
	bottom = false,
	start = false,
	spaceBetween = false,
	className = "",
	utility = { ...utilityProps },
	sx = { ...sxProps },
	spacing = { ...spacingProps },
	children,
	...props
}) => {
	// eslint-disable-next-line no-nested-ternary
	const alignItemsProps = start ? "start" : middle ? "center" : bottom ? "end" : null;
	// eslint-disable-next-line no-nested-ternary
	const justifyContentProps = center ? "center" : justify ? "between" : null;
	// eslint-disable-next-line no-nested-ternary
	const alignContentProps = spaceBetween ? "between" : null;
	return (
		<Tag
			ref={forwardRef}
			className={classes(
				position,
				mx && "grid-margin-x",
				px && "grid-padding-x",
				my && "grid-margin-y",
				py && "grid-padding-y",
				smCols && `small-up-${smCols}`,
				mdCols && `medium-up-${mdCols}`,
				lgCols && `large-up-${lgCols}`,
				...utilityClassName(
					deepMergeProps(
						{
							align: { items: alignItemsProps, content: alignContentProps },
							justify: justifyContentProps,
						},
						pureProps(utility),
					),
				),
				...sxClassName(sx),
				...spacingClassName(spacing),
				className,
			)}
			{...props}
		>
			{children}
		</Tag>
	);
};

Row.propTypes = {
	forwardRef: forwardRefPropType,
	Tag: string,
	position: oneOf(["grid-x", "grid-y"]),
	mx: bool,
	px: bool,
	my: bool,
	py: bool,
	smCols: oneOf([1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12]),
	mdCols: oneOf([1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12]),
	lgCols: oneOf([1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12]),
	center: bool,
	middle: bool,
	justify: bool,
	bottom: bool,
	spaceBetween: bool,
	className: string,
	children: node,
	utility: utilityPropTypes,
	sx: sxPropTypes,
	spacing: spacingPropTypes,
	start: bool,
};

export const Col = ({
	Tag = "div",
	forwardRef,
	auto = false,
	shrink = false,
	sm = "",
	md = "",
	lg = "",
	smOffset,
	mdOffset,
	lgOffset,
	smOrder,
	mdOrder,
	lgOrder,
	className = "",
	center = false,
	children,
	utility = { ...utilityProps },
	sx = { ...sxProps },
	spacing = { ...spacingProps },
	...props
}) => {
	const alignProp = center ? { justify: "center" } : {};
	return (
		<Tag
			ref={forwardRef}
			className={classes(
				"cell",
				auto && "auto",
				shrink && "shrink",
				sm && `small-${sm}`,
				md && `medium-${md}`,
				lg && `large-${lg}`,
				smOffset && `small-offset-${smOffset}`,
				mdOffset && `medium-offset-${mdOffset}`,
				lgOffset && `large-offset-${lgOffset}`,
				smOrder && `small-order-${smOrder}`,
				mdOrder && `medium-order-${mdOrder}`,
				lgOrder && `large-order-${lgOrder}`,
				...utilityClassName(deepMergeProps({ ...alignProp }, pureProps(utility))),
				...sxClassName(sx),
				...spacingClassName(spacing),
				className,
			)}
			{...props}
		>
			{children}
		</Tag>
	);
};

Col.propTypes = {
	Tag: elementType,
	auto: bool,
	shrink: bool,
	center: bool,
	sm: oneOf(["", "auto", "shrink", 1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12]),
	md: oneOf(["", "auto", "shrink", 1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12]),
	lg: oneOf(["", "auto", "shrink", 1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12]),
	smOffset: oneOf([1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12]),
	mdOffset: oneOf([1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12]),
	lgOffset: oneOf([1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12]),
	smOrder: oneOf([0, 1, 2, 3, 4, 5, 6]),
	mdOrder: oneOf([0, 1, 2, 3, 4, 5, 6]),
	lgOrder: oneOf([0, 1, 2, 3, 4, 5, 6]),
	className: string,
	utility: utilityPropTypes,
	sx: sxPropTypes,
	spacing: spacingPropTypes,
	children: node,
	forwardRef: forwardRefPropType,
};
